import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useAnimatedNumber } from "../pages/Estimate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const POPULAR = ["google", "meta", "tiktok", "amazon", "netflix", "spotify", "uber", "airbnb"];

export const EstimatorTeaser = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState(new Set(["google", "meta"]));

  useEffect(() => {
    axios
      .get(`${API}/estimate/services`)
      .then((r) => setServices(r.data.services.filter((s) => POPULAR.includes(s.id))))
      .catch(() => {});
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const total = services.reduce((sum, s) => sum + (selected.has(s.id) ? s.value : 0), 0);
  const animated = useAnimatedNumber(total);

  return (
    <section id="estimate" data-testid="estimator-teaser-section" className="py-24 lg:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="text-xs font-medium uppercase tracking-[0.25em] text-mint mb-4">{t("teaser.kicker")}</p>
          <h2 className="font-display font-semibold tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl text-balance">
            {t("teaser.title")}
          </h2>
          <p className="mt-4 text-base md:text-lg text-white/55 max-w-xl">{t("teaser.sub")}</p>
          <a
            href="/estimateur"
            data-testid="teaser-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-mint px-7 py-3.5 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105"
          >
            {t("teaser.cta")}
            <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </a>
          <p className="mt-4 text-[11px] text-white/30 max-w-md leading-relaxed">{t("teaser.note")}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="rounded-3xl border border-white/8 bg-card p-8"
        >
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/40 mb-4">{t("teaser.tryLabel")}</p>
          <div className="grid grid-cols-2 gap-3">
            {services.map((s) => (
              <button
                key={s.id}
                onClick={() => toggle(s.id)}
                data-testid={`teaser-service-${s.id}`}
                className={`rounded-xl border px-4 py-3 text-sm text-left transition-colors duration-200 ${
                  selected.has(s.id) ? "border-mint/60 bg-mint/10 text-white" : "border-white/10 bg-night/60 text-white/60 hover:border-white/25"
                }`}
              >
                <span className="flex items-center justify-between gap-2">
                  {s.name}
                  {selected.has(s.id) && <CheckCircle2 className="w-4 h-4 text-mint shrink-0" strokeWidth={2} />}
                </span>
              </button>
            ))}
          </div>
          <div className="mt-8 pt-6 border-t border-white/8">
            <p data-testid="teaser-counter" className="font-display font-semibold tracking-tighter text-mint text-4xl sm:text-5xl">
              ≈ {animated} €<span className="text-white/40 text-xl"> / {t("estimate.year")}</span>
            </p>
            <p className="mt-1.5 text-xs text-white/40">{t("teaser.counterLabel")}</p>
            <p className="mt-3 text-[11px] text-white/30">{t("teaser.excerpt")}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
